import { deleteWarehouseThunk, getWarehousesThunk } from '@redux/features/warehouse.slice';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import React, { FC, useEffect, useState } from 'react';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import AddWareHouseModal from './AddWareHouseModal';
import UpdateWareHouseModal from './UpdateWareHouseModal';

const WareHouse: FC = () => {
    const [page, setPage] = useState<number>(0);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [warehouseId, setWarehouseId] = useState<number>();

    const dispatch = useAppDispatch();
    const { warehouses, loading } = useAppSelector(
        (state: RootState) => (state as any).warehouseState || { warehouses: [], loading: false }
    );

    const changePage = (p: number) => setPage(p);

    useEffect(() => {
        dispatch(getWarehousesThunk());
    }, [page, dispatch]);

    const handleConfirmDelete = async () => {
        if (warehouseId) {
            dispatch(deleteWarehouseThunk(warehouseId)).then((response: any) => {
                if (response.meta.requestStatus === 'fulfilled') {
                    setShowDeleteModal(false);
                    setWarehouseId(undefined);
                    dispatch(getWarehousesThunk());
                }
            });
        }
    };

    const handleDelete = (id: number) => {
        setWarehouseId(id);
        setShowDeleteModal(true);
    };

    const handleUpdate = (id: number) => {
        setWarehouseId(id);
        setShowUpdateModal(true);
    };

    return (
        <>
            <UpdateWareHouseModal
                show={showUpdateModal}
                handleClose={() => setShowUpdateModal(false)}
                warehouseId={warehouseId || 0}
            />
            <AddWareHouseModal show={showAddModal} handleClose={() => setShowAddModal(false)} />
            <ConfirmationModal
                show={showDeleteModal}
                handleClose={() => {
                    setShowDeleteModal(false);
                    setWarehouseId(undefined);
                }}
                onConfirm={handleConfirmDelete}
                title="Delete Warehouse"
                message="Are you sure you want to delete this warehouse? This action cannot be undone."
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
                id={warehouseId}
            />
            <BreadCrumb pageTitle={`Warehouses List`} />
            <section className="py-5">
                <div className=" p-3 flex flex-col gap-2 bg-[#fff]">
                    <div className="flex w-full items-center justify-between">
                        <h3 className="font-extrabold text-[#bd902e]">Warehouses List</h3>
                        <ButtonBase innerText="Add Warehouse" onClick={() => setShowAddModal(true)} />
                    </div>
                    {loading ? (
                        <AnimatedListSkeleton rows={8} />
                    ) : (
                        <>
                            <div>
                                <div className="overflow-x-auto">
                                    <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                                        <thead className="ltr:text-left rtl:text-right">
                                            <tr
                                                className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                            >
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Name</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Location</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase">Shelf Number</th>
                                                <th className="px-3.5 py-2.5 font-semibold uppercase text-center">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <AnimatedTableBody loading={loading} itemCount={(warehouses || []).length} animationKey={`warehouse-${page}`}>
                                            {(warehouses || []).map((wh: any) => (
                                                <AnimatedTableRow
                                                    rowKey={wh?.id}
                                                    detailRecord={wh}
                                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                                >
                                                    <td className="px-3.5 py-2.5">{wh?.id || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{wh?.name || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{wh?.location || `-`}</td>
                                                    <td className="px-3.5 py-2.5">{wh?.dunsNumber || `-`}</td>
                                                    <td className="px-3.5 py-2.5">
                                                        <div className="flex flex-row justify-center items-center gap-2 w-full">
                                                            <button
                                                                className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-red-500 text-red-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-red-50 hover:border-red-600 hover:scale-105 active:scale-95"
                                                                onClick={() => {
                                                                    handleDelete(wh?.id);
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                            <button
                                                                className="group flex px-4 py-2 text-xs font-semibold items-center justify-center
                                                        rounded-md border border-yellow-600 text-yellow-600
                                                        bg-white transition-all duration-300 ease-linear hover:bg-yellow-50 hover:border-yellow-700 hover:scale-105 active:scale-95"
                                                                onClick={() => {
                                                                    handleUpdate(wh?.id);
                                                                }}
                                                            >
                                                                Update
                                                            </button>
                                                        </div>
                                                    </td>
                                                </AnimatedTableRow>
                                            ))}
                                        </AnimatedTableBody>
                                    </AnimatedTable>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default WareHouse;
